import { SettingsType } from "App.types";
import { RecordHistoryRecordType } from "organisms/RecordHistory/RecordHistory.types";
import { BookingRecordType, InvoiceRecordType } from "pages/Booking/Booking.types";
import { EventBaseRecordType } from "pages/EventBase/EventBase.types";
import { EventLocationRecordType } from "pages/EventLocation/EventLocation.types";
import { FileRecordType } from "pages/SharedRecords.types";
import { DeleteParams, DeleteResult, FilterPayload, GetListParams, SortPayload } from "react-admin";

import dataProvider from "./dataProvider";

export const defaultListFetchParams: GetListParams = {
  pagination: { page: 1, perPage: 1000 },
  sort: { field: "name", order: "DESC" },
  filter: {},
};

export async function fetchListData(resource: string, filter?: FilterPayload, sort?: SortPayload) {
  let params = defaultListFetchParams;
  if (filter) {
    params = { ...params, filter };
  }
  if (sort) {
    params = { ...params, sort };
  }
  const result = await dataProvider.getList(resource, params);

  return result;
}

async function deleteData(resource: string, params: DeleteParams) {
  const result = await dataProvider.delete(resource, params);
  return result.data as DeleteResult;
}

export async function fetchBooking(id: number) {
  const result = await dataProvider.getOne<BookingRecordType>("bookings", { id });
  return result.data;
}

export async function fetchLocations(filter: FilterPayload) {
  const result = await fetchListData("event_locations", filter);
  return result.data as EventLocationRecordType[];
}

export async function fetchEventBase(id: number) {
  const result = await dataProvider.getOne<EventBaseRecordType>("event_bases", { id });
  return result.data;
}

export async function fetchEventBaseOrParent(id: number) {
  let eventBase = await fetchEventBase(id);
  const parent = eventBase.group_parent;
  if (parent) {
    eventBase = await fetchEventBase(parent);
  }
  return eventBase;
}

export async function fetchEnrollmentsCount() {
  const params = {
    ...defaultListFetchParams,
    filter: { processed: false, payment_provider: "EDM_INVOICE", omit: "~all", limit: 1 },
  };

  const result = await dataProvider.getList("enrollments", params);
  return result.total || 0;
}

export async function deleteNote(params: DeleteParams) {
  return await deleteData("notes", params);
}

export async function deleteInvoice(params: DeleteParams) {
  return await deleteData("invoices", params);
}

export async function fetchSettings(filter: FilterPayload) {
  const result = await fetchListData("settings", filter);

  return result.data as SettingsType[];
}

export async function fetchHistoricalChanges(resource: string, id: number) {
  const result = await fetchListData(`${resource}/${id}/history`);

  return result.data as RecordHistoryRecordType[];
}

export async function createInvoices(data: Partial<InvoiceRecordType>[]) {
  const result = await dataProvider.create<InvoiceRecordType>("invoices", { data });
  return result.data as unknown as InvoiceRecordType[];
}

export async function uploadFile({
  fileData,
  relatedResource,
  relatedId,
  originalFilename,
  tags = [],
  visibleForAll = false,
}: {
  fileData: unknown;
  relatedResource: string;
  relatedId: number;
  originalFilename: string;
  tags: string[];
  visibleForAll: boolean;
}) {
  const data = {
    data: fileData,
    related_resource: relatedResource,
    related_id: relatedId,
    original_filename: originalFilename,
    visible_for_all: visibleForAll,
    tags,
  };
  const result = await dataProvider.create<FileRecordType>("files", { data });
  return result.data;
}
